﻿"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import {
  createSession,
  listSessions,
  deleteSession,
  getSession,
  ensureUserId,
} from "@/lib/api";
import { prettyProvider, prettyModelId } from "@/lib/aiMap";

type Session = {
  id: number;
  title?: string;
  preview?: string;
  // Timestamps (support multiple casings/keys from backend)
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  // Model/provider metadata
  provider?: string | null;
  model?: string | null;
  // Optional embedded messages
  messages?: any[];
};

export default function RecentChats() {
  const router = useRouter();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Session[] | null>(null);
  const [suppressSearch, setSuppressSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function reload() {
    setLoading(true);
    try {
      // Ensure we have a user ID before filtering
      await ensureUserId();
      const userId = localStorage.getItem('user_id');
      
      const data: any = await listSessions();
      const arr = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];
      // If logged in, only show sessions belonging to this user
      let filteredArr = arr as Session[];
      if (userId) {
        filteredArr = (arr as any[]).filter((it) => {
          const uid = (it as any)?.user_id ?? (it as any)?.userId ?? null;
          return uid != null && String(uid) === userId;
        }) as Session[];
      }
      setSessions(filteredArr);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  // Match against session title/preview and any message content if available
  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    if (!text) return sessions;

    const matchesSessionMeta = (s: Session) => {
      const title = (displayTitle(s) || `Session #${s.id}`).toLowerCase();
      return title.includes(text);
    };

    const matchesAnyMessage = (s: Session) => {
      const msgs: any[] = Array.isArray((s as any).messages)
        ? (s as any).messages
        : [];
      if (!msgs.length) return false;
      return msgs.some((m: any) => {
        const raw = (m?.content ?? m?.text ?? m?.message ?? "").toString();
        return raw.toLowerCase().includes(text);
      });
    };

    return sessions.filter(
      (s) => matchesSessionMeta(s) || matchesAnyMessage(s)
    );
  }, [q, sessions]);

  // Auto search: fast client-side scan using only getSession(id)
  useEffect(() => {
    const term = q.trim();
    if (suppressSearch) return;
    if (!term || term.length < 2) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const lower = term.toLowerCase();
        // Build a base list sorted by recency; limit to prevent too many requests
        let base = sessions;
        if (!base || base.length === 0) {
          try {
            const raw: any = await listSessions();
            let parsed = (
              Array.isArray(raw?.items)
                ? raw.items
                : Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.data)
                ? raw.data
                : []
            ) as Session[];
            // Apply the same user filter if logged in
            try {
              const userId = localStorage.getItem('user_id');
              if (userId) {
                parsed = (parsed as any[]).filter((it) => {
                  const uid = (it as any)?.user_id ?? (it as any)?.userId ?? null;
                  return uid != null && String(uid) === userId;
                }) as Session[];
              }
            } catch {}
            base = parsed;
          } catch {}
        }
        const parseDate = (v: any) => (v ? Date.parse(String(v)) : 0);
        const sorted = [...(base || [])].sort(
          (a: any, b: any) =>
            parseDate(b?.updatedAt) - parseDate(a?.updatedAt) ||
            Number(b?.id) - Number(a?.id)
        );
        const limit = 40;
        const pool = 6; // concurrency
        const queue = sorted.slice(0, limit);
        const results: Session[] = [];
        let idx = 0;
        // spin workers
        const worker = async () => {
          while (!cancelled && idx < queue.length) {
            const current = queue[idx++];
            try {
              const detail: any = await getSession(current.id);
              const msgs: any[] = Array.isArray(detail?.messages)
                ? detail.messages
                : Array.isArray(detail?.history)
                ? detail.history
                : Array.isArray(detail?.items)
                ? detail.items
                : [];
              const match = msgs.some((m: any) =>
                String(m?.content ?? m?.text ?? m?.message ?? "")
                  .toLowerCase()
                  .includes(lower)
              );
              if (match) results.push({ ...(current as any), messages: msgs });
            } catch {}
          }
        };
        await Promise.all(new Array(pool).fill(0).map(() => worker()));
        if (!cancelled) setSearchResults(results);
      } catch {
        // Swallow errors (no alerts); fall back to client filter
        setSearchResults(null);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q, suppressSearch]);

  function firstUserText(s: Session): string | undefined {
    const msgs: any[] = Array.isArray((s as any).messages)
      ? (s as any).messages
      : [];
    const userMsg = msgs.find((m) => (m?.role || m?.sender) === "user");
    return (
      (userMsg?.text || userMsg?.content || userMsg?.message || "").trim() ||
      undefined
    );
  }

  function displayTitle(s: Session): string {
    return (
      (s.title && s.title.trim()) ||
      (s.preview && s.preview.trim()) ||
      firstUserText(s) ||
      (s as any).firstMessage ||
      (s as any).first_message ||
      `Session #${s.id}`
    );
  }

  function getProvider(s: Session): string | undefined {
    const raw = (s as any).provider ?? (s as any).vendor ?? (s as any).source;
    if (!raw) return undefined;
    const p = String(raw).trim();
    // ซ่อน provider ถ้าเป็น auto / openrouter
    if (/^(auto|openrouter|openrounter)$/i.test(p)) return undefined;
    return prettyProvider(p);
  }

  function getModel(s: Session): string | undefined {
    const m = (s as any).model ?? (s as any).model_name ?? (s as any).modelName;
    if (!m) return undefined;
    return prettyModelId(String(m));
  }

  function pickDate(s: Session): string | undefined {
    return (
      (s as any).created_at ||
      (s as any).createdAt ||
      (s as any).updated_at ||
      (s as any).updatedAt ||
      undefined
    );
  }

  function formatDate(input?: string): string {
    if (!input) return "";
    const d = new Date(input);
    if (isNaN(d.getTime())) return String(input);
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = pad(d.getDate());
    const mon = months[d.getMonth()];
    const year = d.getFullYear();
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${day} ${mon} ${year}, ${hh}:${mm}`;
  }

  const onOpen = (id: number) => {
    localStorage.setItem("active_session_id", String(id));
    window.dispatchEvent(new CustomEvent("sessionSwitched", { detail: id }));
    // Avoid navigation here to prevent search re-trigger; ChatBot listens to sessionSwitched
    // try { router.push(`/c/${id}`); } catch {}
  };

  function pickSessionId(s: any): number | null {
    const cand = s?.id ?? s?.session_id ?? s?.sessionId ?? null;
    const n = Number(cand);
    return Number.isFinite(n) ? n : null;
  }

  const onOpenSession = (s: any) => {
    const id = pickSessionId(s);
    if (id == null) return;
    setSuppressSearch(true);
    // Keep suppression short; navigation will replace view
    setTimeout(() => setSuppressSearch(false), 800);
    try {
      inputRef.current?.blur();
    } catch {}
    try {
      setQ("");
    } catch {}
    try {
      setSearchResults(null);
    } catch {}
    // Best-effort store conversation id for faster load on ChatBot
    try {
      const conv =
        (s as any)?.conversation?.id ??
        (s as any)?.conversationId ??
        (s as any)?.conversation_id ??
        (s as any)?.conv_id ??
        (Array.isArray((s as any)?.messages)
          ? (s as any)?.messages?.[0]?.conversation_id
          : null);
      if (conv != null)
        localStorage.setItem("active_conversation_id", String(conv));
    } catch {}
    localStorage.setItem("active_session_id", String(id));
    window.dispatchEvent(new CustomEvent("sessionSwitched", { detail: id }));
    // Navigate after a very short delay so ChatBot mounts the correct session
    setTimeout(() => {
      try {
        router.push(`/c/${id}`);
      } catch {}
    }, 50);
  };

  const onNew = async () => {
    try {
      const api = await import("@/lib/api");
      await api.ensureUserId();
    } catch {}
    
    try {
      const uid = (typeof window !== 'undefined') ? localStorage.getItem('user_id') : undefined;
      const s = await createSession();
      if (s?.id) {
        onOpen(s.id);
        // best-effort attach user_id immediately after creation (both session and its conversation)
        try {
          if (uid) {
            const api = await import("@/lib/api");
            await api.updateSession(s.id, { user_id: uid, userId: uid, owner_id: uid, ownerId: uid, created_by: uid, createdBy: uid });
            // Fetch detail to discover the actual conversation id, then update it
            try {
              const detail: any = await getSession(s.id);
              const conv = detail?.conversation?.id || detail?.conversation_id || detail?.conversationId || detail?.conv_id || null;
              if (conv != null) {
                await api.updateConversation(conv, { user_id: uid, userId: uid, owner_id: uid, ownerId: uid, created_by: uid, createdBy: uid });
                try { localStorage.setItem('active_conversation_id', String(conv)); } catch {}
              }
            } catch {}
          }
        } catch {}
        reload();
      }
    } catch (err: any) {
      console.error('Create session failed', err);
      try { alert(`สร้างห้องไม่สำเร็จ: ${err?.message || 'unknown error'}`); } catch {}
    }
  };

  // Pick which list to render: server search results when typing, else local filtered list
  const listToShow: Session[] = (() => {
    const hasQ = q.trim().length > 0;
    if (hasQ) {
      // If server search failed or pending, fall back to client-side filtered list
      return (searchResults ?? filtered) as Session[];
    }
    return filtered as Session[];
  })();

  return (
    <div className="flex h-full w-full flex-col bg-gray-900">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-gray-700 text-gray-200 text-sm font-medium">
        <span>Recent Chats</span>
      </div>

      {/* Search */}
      <div className="p-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search sessions"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            ref={inputRef}
            className="w-full rounded-md bg-gray-800 text-gray-200 placeholder:text-gray-400 py-2.5 pl-10 pr-3 outline-none ring-1 ring-gray-700 focus:ring-2 focus:ring-indigo-500"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        {/* no server search banner */}
      </div>

      {/* Explore banner (kept) */}
      <div className="px-3">
        <button
          onClick={onNew}
          className="w-full text-left rounded-md border border-gray-600 bg-gray-800/80 px-3 py-2 text-gray-100 flex items-center gap-2 hover:bg-gray-800 transition-colors"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded bg-gray-700">
            <ChatBubbleOutlineIcon fontSize="small" />
          </span>
          <div className="text-sm font-medium">New Chat</div>
        </button>
        <div className="rounded-md border border-gray-600 bg-gray-800/80 px-3 py-2 text-gray-100 flex items-center gap-2 mt-2">
          <span className="flex h-8 w-8 items-center justify-center rounded bg-gray-700">
            <GridViewOutlined fontSize="small" />
          </span>
          <div className="text-sm font-medium">Explore Sphere</div>
          <div className="ml-auto flex -space-x-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-white text-[10px] ring-2 ring-gray-800">
              +60
            </span>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="mt-3 flex-1 overflow-y-auto px-3 pb-3 space-y-3 custom-scroll">
        {(loading || searching) && (
          <div className="text-center text-gray-400 text-sm">Loading…</div>
        )}
        {!loading && !searching && listToShow.length === 0 && (
          <div className="text-center text-gray-400 text-sm">No sessions</div>
        )}
        {listToShow.map((s) => (
          <div
            key={s.id}
            onClick={() => onOpenSession(s)}
            className="w-full rounded-md border border-gray-600 bg-gray-800/70 p-3 text-gray-100 hover:bg-gray-800 transition-colors cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSession(s); } }}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 text-left">
                <h3 className="text-sm font-semibold leading-snug line-clamp-2">
                  {displayTitle(s)}
                </h3>
                <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
                  <span className="inline-flex items-center gap-1 rounded bg-gray-700 px-1.5 py-0.5">
                    {(() => {
                      const prov = getProvider(s);
                      const mdl = getModel(s);
                      if (mdl) {
                        return (
                          <>
                            {prov && (
                              <span className="uppercase tracking-wide text-gray-300">
                                {prov}
                              </span>
                            )}
                            <span className="text-gray-200">{mdl}</span>
                          </>
                        );
                      }
                      // หากไม่มี model ให้แสดง provider ถ้ามี, มิฉะนั้นแสดง Chat
                      return prov ? (
                        <span className="uppercase tracking-wide text-gray-300">
                          {prov}
                        </span>
                      ) : (
                        <span>Chat</span>
                      );
                    })()}
                  </span>
                  <span>{formatDate(pickDate(s))}</span>
                </div>
              </div>
              <button
                title="Delete session"
                onClick={async (e) => {
                  e.stopPropagation();
                  const ok = window.confirm("Delete this session?");
                  if (!ok) return;
                  try {
                    await deleteSession(s.id);
                  } catch (err: any) {
                    alert(`Delete failed: ${err?.message || "unknown error"}`);
                  } finally {
                    const active = localStorage.getItem("active_session_id");
                    if (active && Number(active) === s.id) {
                      localStorage.removeItem("active_session_id");
                    }
                    reload();
                  }
                }}
                className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-gray-700/70 text-gray-300"
              >
                <DeleteOutline fontSize="small" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer removed */}
    </div>
  );
}
